/**
 * A-LIGN Audit Kickoff — full admin page.
 *
 * Three columns:
 *   1. Pre-audit checklist (live-verified statuses from /checklist)
 *   2. Email draft editor + "Generate" + "Send via Resend"
 *   3. Auditor handoff bundle (signed JSON + PDF link)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Mail, CheckCircle2, AlertTriangle, ListChecks, Clock, FileCheck,
  Copy, Send, ExternalLink, ShieldCheck, Hand,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  ok: { cls: 'text-emerald-300', label: 'Ready', Icon: CheckCircle2 },
  evidence_url_exists: { cls: 'text-cyan-300', label: 'Evidence URL', Icon: ExternalLink },
  manual_required: { cls: 'text-amber-300', label: 'Manual check', Icon: Hand },
  below_threshold: { cls: 'text-rose-300', label: 'Below target', Icon: AlertTriangle },
  endpoint_unreachable: { cls: 'text-rose-300', label: 'Endpoint down', Icon: AlertTriangle },
  unknown: { cls: 'text-slate-400', label: 'Unknown', Icon: Clock },
};

export default function AlignKickoff() {
  const [checklist, setChecklist] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [emailDraft, setEmailDraft] = useState(null);
  const [emailForm, setEmailForm] = useState({
    auditor_email: 'engagement@a-lign.com',
    audit_target_date: '2026-06-15',
    sender_name: 'Lakshmi Kiranmai Guduru',
    sender_email: 'lakshmi@cartie.ai',
  });
  const [draftBusy, setDraftBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [copied, setCopied] = useState(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const [cRes, bRes] = await Promise.all([
      fetch(`${API}/api/align-kickoff/checklist`, { headers }),
      fetch(`${API}/api/align-kickoff/handoff-package`, { headers }),
    ]);
    if (cRes.ok) setChecklist(await cRes.json());
    if (bRes.ok) {
      const b = await bRes.json();
      setBundle(b);
      if (b.email_draft) setEmailDraft(b.email_draft);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const generateDraft = async () => {
    setDraftBusy(true);
    setSendResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/align-kickoff/generate-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm),
      });
      if (r.ok) setEmailDraft(await r.json());
    } finally { setDraftBusy(false); }
  };

  const sendEmail = async () => {
    if (!window.confirm(`Send kickoff email to ${emailForm.auditor_email} now?`)) return;
    setSendBusy(true);
    setSendResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/align-kickoff/send-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(emailForm),
      });
      const data = await r.json();
      if (r.ok) setSendResult({ ok: true, ...data });
      else setSendResult({ ok: false, error: data.detail || `HTTP ${r.status}` });
    } catch (e) {
      setSendResult({ ok: false, error: String(e.message || e) });
    } finally { setSendBusy(false); }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* clipboard blocked */ }
  };

  const sBadge = (status) => {
    const m = STATUS_META[status] || STATUS_META.unknown;
    const Icon = m.Icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-slate-800 border border-slate-700 ${m.cls}`}>
        <Icon className="w-2.5 h-2.5" /> {m.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="align-kickoff-page">
      <Helmet><title>A-LIGN Audit Kickoff | CARTIEAI</title></Helmet>

      {/* Header — vibrant hero */}
      <div className="mb-10">
        <div className="flex items-start gap-4 mb-4">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500 bg-gradient-to-br from-emerald-500 to-cyan-500 items-center justify-center shadow-xl shadow-emerald-500/40 shrink-0">
            <ShieldCheck className="w-7 h-7 text-white drop-shadow-sm" />
          </div>
          <div className="flex-1">
            <Badge className="bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 text-[10px] font-mono tracking-widest mb-2">SOC 2 Type II</Badge>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 drop-shadow-[0_2px_18px_rgba(16,185,129,0.25)]">
              <span className="text-white">A-LIGN </span><span className="text-emerald-400">Audit Kickoff</span>
            </h1>
          </div>
        </div>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
          Three artifacts to get the SOC 2 Type II audit moving: <span className="text-emerald-300 font-semibold">a verified pre-audit checklist</span>, a <span className="text-emerald-300 font-semibold">ready-to-send kickoff email</span>, and a <span className="text-emerald-300 font-semibold">signed evidence bundle</span> A-LIGN can review offline.
        </p>
      </div>

      {/* Checklist + summary */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/20 mb-6" data-testid="align-checklist-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <ListChecks className="w-5 h-5 text-emerald-400" /> Pre-audit checklist
            </CardTitle>
            {checklist?.summary && (
              <div className="text-xs text-slate-400">
                <span className="text-emerald-300 font-bold" data-testid="align-summary-ok">{checklist.summary.ok}</span> ok ·{' '}
                <span className="text-cyan-300 font-bold">{checklist.summary.evidence_only}</span> evidence ·{' '}
                <span className="text-amber-300 font-bold">{checklist.summary.manual}</span> manual ·{' '}
                <span className="text-rose-300 font-bold">{checklist.summary.below_threshold}</span> below
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!checklist ? (
            <div className="space-y-2">{[0, 1, 2, 3].map(i => <div key={i} className="h-10 bg-slate-800 rounded animate-pulse" />)}</div>
          ) : (
            <div className="space-y-2">
              {checklist.items.map((it) => (
                <div key={it.id} className="flex items-start justify-between gap-3 rounded-md border border-emerald-500/20 bg-slate-950 px-3 py-2.5" data-testid={`align-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white">{it.id}. {it.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{it.rationale}</div>
                    {it.actual_value !== null && it.actual_value !== undefined && (
                      <div className="text-xs text-cyan-300 mt-0.5 font-mono">value: {it.actual_value}{it.threshold ? ` (target ≥ ${it.threshold})` : ''}</div>
                    )}
                    {it.verify_url && (
                      <a href={it.verify_url} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-0.5 mt-1">
                        {it.verify_url} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    )}
                  </div>
                  <div className="shrink-0">{sBadge(it.status)}</div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-400 italic mt-3">
            Live-verified statuses (where endpoint exists). Manual items must be checked in person before kickoff.
          </div>
        </CardContent>
      </Card>

      {/* Email draft */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/30 mb-6" data-testid="align-email-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-400" /> Auditor kickoff email
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs text-slate-300 uppercase tracking-widest font-semibold mb-1 block">Auditor email</label>
              <input
                type="email" value={emailForm.auditor_email}
                onChange={(e) => setEmailForm({ ...emailForm, auditor_email: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                data-testid="align-auditor-email-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-300 uppercase tracking-widest font-semibold mb-1 block">Audit target date</label>
              <input
                type="date" value={emailForm.audit_target_date}
                onChange={(e) => setEmailForm({ ...emailForm, audit_target_date: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-300 uppercase tracking-widest font-semibold mb-1 block">Your name</label>
              <input
                type="text" value={emailForm.sender_name}
                onChange={(e) => setEmailForm({ ...emailForm, sender_name: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              />
            </div>
            <div>
              <label className="text-xs text-slate-300 uppercase tracking-widest font-semibold mb-1 block">Your email (reply-to)</label>
              <input
                type="email" value={emailForm.sender_email}
                onChange={(e) => setEmailForm({ ...emailForm, sender_email: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button onClick={generateDraft} disabled={draftBusy} className="bg-cyan-600 hover:bg-emerald-500 text-white" data-testid="align-generate-btn">
              {draftBusy ? 'Generating…' : 'Generate draft'}
            </Button>
            <Button onClick={sendEmail} disabled={sendBusy || !emailDraft} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="align-send-btn">
              <Send className="w-4 h-4 mr-1.5" /> {sendBusy ? 'Sending…' : 'Send via Resend'}
            </Button>
          </div>

          {sendResult && (
            <div className={`mb-4 rounded-md px-3 py-2 text-xs ${sendResult.ok ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300' : 'bg-rose-500/10 border border-rose-500/30 text-rose-300'}`}>
              {sendResult.ok ? <span><CheckCircle2 className="w-3 h-3 inline mr-1" /> Sent to {sendResult.sent_to}</span> : <span><AlertTriangle className="w-3 h-3 inline mr-1" /> {sendResult.error}</span>}
            </div>
          )}

          {emailDraft && (
            <div className="space-y-3" data-testid="align-email-draft">
              <div className="rounded-md border border-emerald-500/20 bg-slate-950 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">Subject</span>
                  <button onClick={() => copyToClipboard(emailDraft.subject, 'subject')} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
                    {copied === 'subject' ? <><CheckCircle2 className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <div className="text-sm text-white font-mono">{emailDraft.subject}</div>
              </div>
              <div className="rounded-md border border-emerald-500/20 bg-slate-950 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">Plaintext body</span>
                  <button onClick={() => copyToClipboard(emailDraft.plaintext, 'plain')} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
                    {copied === 'plain' ? <><CheckCircle2 className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono max-h-72 overflow-y-auto">{emailDraft.plaintext}</pre>
              </div>
              <details className="rounded-md border border-emerald-500/20 bg-slate-950 p-3">
                <summary className="text-[10px] font-bold uppercase tracking-widest text-cyan-300 cursor-pointer">Preview HTML rendering</summary>
                <div className="mt-3 bg-white text-white rounded p-4 text-sm" dangerouslySetInnerHTML={{ __html: emailDraft.html }} />
              </details>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Handoff bundle */}
      <Card className="bg-slate-950 text-slate-100 border-emerald-500/30" data-testid="align-bundle-card">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-cyan-400" /> Signed evidence bundle
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!bundle ? (
            <div className="h-20 bg-slate-800 rounded animate-pulse" />
          ) : (
            <div className="space-y-3 text-sm">
              <p className="text-slate-400">A single, signed artifact the auditor can review offline — JSON for machines, PDF for humans.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bundle.shareable_bundle_url_pdf && (
                  <a href={`${API}${bundle.shareable_bundle_url_pdf}`} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md bg-slate-950 border border-slate-700 hover:border-emerald-500/40 transition-colors" data-testid="align-bundle-pdf">
                    <span className="text-slate-400 inline-flex items-center gap-1.5"><FileCheck className="w-4 h-4 text-emerald-400" /> Evidence bundle (PDF)</span>
                    <ExternalLink className="w-3 h-3 text-emerald-300" />
                  </a>
                )}
                <a href={`${API}/api/soc2-readiness/audit-evidence-bundle`} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md bg-slate-950 border border-slate-700 hover:border-emerald-500/40 transition-colors" data-testid="align-bundle-json">
                  <span className="text-slate-400 inline-flex items-center gap-1.5"><FileCheck className="w-4 h-4 text-cyan-400" /> Evidence bundle (JSON)</span>
                  <ExternalLink className="w-3 h-3 text-cyan-300" />
                </a>
              </div>
              {bundle.signed_bundle_token && (
                <div className="rounded-md bg-slate-950 border border-emerald-500/20 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-300">Signed token (valid 7d)</span>
                    <button onClick={() => copyToClipboard(bundle.signed_bundle_token, 'token')} className="text-xs text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1">
                      {copied === 'token' ? <><CheckCircle2 className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                    </button>
                  </div>
                  <div className="text-[11px] font-mono text-slate-400 break-all">{bundle.signed_bundle_token}</div>
                </div>
              )}
              {bundle.content_hash && (
                <div className="text-[11px] text-slate-400 font-mono">Content hash: {bundle.content_hash}</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
